import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Search, X, AlertTriangle, Info, Package } from 'lucide-react';
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Link } from 'react-router-dom';
import { createPageUrl } from '../utils';

export default function Additives() {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedAdditive, setSelectedAdditive] = useState(null);

    const { data: additives = [], isLoading } = useQuery({
        queryKey: ['additives'],
        queryFn: () => base44.entities.Additive.list('-created_date'),
    });

    const { data: products = [] } = useQuery({
        queryKey: ['products'],
        queryFn: () => base44.entities.Product.list(),
    });

    const filteredAdditives = additives.filter(additive => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            additive.code?.toLowerCase().includes(query) ||
            additive.name?.toLowerCase().includes(query) ||
            additive.description?.toLowerCase().includes(query)
        );
    });

    const getAdditiveProducts = (additive) => {
        if (!additive.product_ids) return [];
        return products.filter(p => additive.product_ids.includes(p.id));
    };

    const getRiskColor = (level) => {
        switch(level) {
            case 'bajo': return 'emerald';
            case 'moderado': return 'amber';
            case 'alto': return 'red';
            default: return 'gray';
        }
    };

    const AdditiveCard = ({ additive, onClick }) => {
        const color = getRiskColor(additive.risk_level);
        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onClick}
                className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
            >
                <div className="flex items-start gap-4">
                    <div className={`
                        w-16 h-16 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0
                        bg-${color}-50 text-${color}-700
                    `}>
                        {additive.code}
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 mb-1">{additive.name}</h3>
                        <p className="text-sm text-gray-600 line-clamp-2 mb-2">{additive.description}</p>
                        <div className="flex items-center gap-2">
                            <span className={`
                                text-xs px-2 py-1 rounded-full font-medium
                                bg-${color}-100 text-${color}-700
                            `}>
                                Riesgo {additive.risk_level}
                            </span>
                            {additive.product_ids?.length > 0 && (
                                <span className="text-xs text-gray-400">
                                    {additive.product_ids.length} producto{additive.product_ids.length !== 1 ? 's' : ''}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </motion.div>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-gray-50 pb-20">
            {/* Header */}
            <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-40">
                <div className="max-w-2xl mx-auto px-4 py-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center shadow-lg shadow-purple-500/20">
                            <Info className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h1 className="font-bold text-gray-900 text-lg">Base de Datos de Aditivos</h1>
                            <p className="text-xs text-gray-400">Información completa sobre aditivos alimentarios</p>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-2xl mx-auto px-4 py-6">
                {/* Search */}
                <div className="relative mb-6">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Buscar por código (ej: E102) o nombre..."
                        className="pl-10 h-12 rounded-xl border-gray-200 bg-white"
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
                        >
                            <X className="w-4 h-4 text-gray-500" />
                        </button>
                    )}
                </div>

                {/* Stats */}
                <div className="bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl p-5 mb-6 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm opacity-90 mb-1">Total de aditivos detectados</p>
                            <p className="text-3xl font-bold">{additives.length}</p>
                        </div>
                        <AlertTriangle className="w-12 h-12 opacity-20" />
                    </div>
                </div>

                {/* Additives List */}
                {isLoading ? (
                    <div className="space-y-4">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="bg-white rounded-2xl p-4 animate-pulse">
                                <div className="flex gap-4">
                                    <div className="w-16 h-16 rounded-xl bg-gray-100" />
                                    <div className="flex-1">
                                        <div className="h-5 bg-gray-100 rounded w-3/4 mb-2" />
                                        <div className="h-4 bg-gray-100 rounded w-full mb-2" />
                                        <div className="h-6 bg-gray-100 rounded w-20" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : filteredAdditives.length === 0 ? (
                    <Card className="p-8 text-center">
                        <Info className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-400">
                            {searchQuery ? 'No se encontraron aditivos' : 'Aún no se han detectado aditivos'}
                        </p>
                    </Card>
                ) : (
                    <div className="space-y-3">
                        {filteredAdditives.map((additive) => (
                            <AdditiveCard
                                key={additive.id}
                                additive={additive}
                                onClick={() => setSelectedAdditive(additive)}
                            />
                        ))}
                    </div>
                )}
            </main>

            {/* Detail Modal */}
            {selectedAdditive && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
                    onClick={() => setSelectedAdditive(null)}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 100 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 100 }}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[80vh] overflow-hidden"
                    >
                        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className={`
                                    w-12 h-12 rounded-xl flex items-center justify-center text-sm font-bold
                                    bg-${getRiskColor(selectedAdditive.risk_level)}-50 
                                    text-${getRiskColor(selectedAdditive.risk_level)}-700
                                `}>
                                    {selectedAdditive.code}
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900">{selectedAdditive.name}</h2>
                                    <span className={`
                                        text-xs px-2 py-1 rounded-full font-medium
                                        bg-${getRiskColor(selectedAdditive.risk_level)}-100 
                                        text-${getRiskColor(selectedAdditive.risk_level)}-700
                                    `}>
                                        Riesgo {selectedAdditive.risk_level}
                                    </span>
                                </div>
                            </div>
                            <button
                                onClick={() => setSelectedAdditive(null)}
                                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
                            >
                                <X className="w-5 h-5 text-gray-600" />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto max-h-[60vh]">
                            {selectedAdditive.description && (
                                <div className="mb-6">
                                    <h3 className="font-semibold text-gray-900 mb-2">Descripción</h3>
                                    <p className="text-gray-600">{selectedAdditive.description}</p>
                                </div>
                            )}

                            {selectedAdditive.health_effects && (
                                <div className="mb-6">
                                    <h3 className="font-semibold text-gray-900 mb-2">Efectos en la salud</h3>
                                    <p className="text-gray-600">{selectedAdditive.health_effects}</p>
                                </div>
                            )}

                            {selectedAdditive.common_uses && (
                                <div className="mb-6">
                                    <h3 className="font-semibold text-gray-900 mb-2">Usos comunes</h3>
                                    <p className="text-gray-600">{selectedAdditive.common_uses}</p>
                                </div>
                            )}

                            {selectedAdditive.product_ids?.length > 0 && (
                                <div>
                                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                        <Package className="w-4 h-4" />
                                        Productos que contienen este aditivo
                                    </h3>
                                    <div className="space-y-2">
                                        {getAdditiveProducts(selectedAdditive).map((product) => (
                                            <Link
                                                key={product.id}
                                                to={createPageUrl('Home')}
                                                className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                                            >
                                                {product.image_url ? (
                                                    <img
                                                        src={product.image_url}
                                                        alt={product.name}
                                                        className="w-12 h-12 rounded-lg object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-12 h-12 rounded-lg bg-gray-200" />
                                                )}
                                                <div className="flex-1">
                                                    <p className="font-medium text-gray-900">{product.name}</p>
                                                    {product.brand && (
                                                        <p className="text-sm text-gray-500">{product.brand}</p>
                                                    )}
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </div>
    );
}